/**
 * Dashboard data hooks — one query per backend endpoint.
 *
 * Each card on the dashboard subscribes to its own query so a failure
 * in one (e.g. market state) doesn't blank out the whole screen. Cache
 * windows are tuned by data velocity:
 *
 *   summary       — 15s   (KPI strip changes only on close/signal)
 *   equity curve  — 60s   (bucketed, slow-moving)
 *   open positions— 5s    (price updates ride here)
 *   recent signals— 7s    (Phase 4 polls, Phase 8 replaces with WS)
 *   market state  — 20s   (CVD/OI move on minutes, not seconds)
 *   risk usage    — 15s
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'

// ─── Types (mirror api.schemas.*) ────────────────────────────────────

export interface DashboardSummary {
  realized_pnl_today: number
  realized_pnl_today_pct: number
  unrealized_pnl: number
  open_positions: number
  win_rate_7d: number | null
  trades_7d: number
  trades_today: number
  signals_1h: number
  signals_1h_matched: number
}

export interface EquityPoint {
  bucket: string
  total_equity: number
  realized_pnl: number
  unrealized_pnl: number
}

export interface EquityCurveResponse {
  points: EquityPoint[]
  days: number
}

export interface MarketState {
  btc_phase: string | null
  stress_level: 'normal' | 'elevated' | 'critical'
  btc_oi_24h_pct: number | null
  cvd_1h_usd: number | null
  signals_1h_total: number
  updated_at: string
}

export interface RiskUsage {
  daily_pnl_pct: number
  daily_loss_limit_pct: number
  trades_today: number
  open_positions: number
  max_concurrent_positions: number
  consecutive_losses: number
}

export interface OpenPosition {
  id: number
  symbol: string
  direction: 'long' | 'short'
  entry_price: number
  current_price: number | null
  quantity: number
  leverage: number
  unrealized_pnl: number | null
  unrealized_pnl_pct: number | null
  take_profit_price: number | null
  stop_loss_price: number | null
  dca_level: number
  opened_at: string
}

export interface OpenPositionsResponse {
  items: OpenPosition[]
  total: number
}

export interface RecentSignal {
  id: number
  ts: string
  symbol: string
  direction: 'long' | 'short'
  signal_type: string
  timeframe: string
  score: number | null
  matched_for_me: boolean
}

export interface RecentSignalsResponse {
  items: RecentSignal[]
}

// ─── Closed positions + detail (history page) ────────────────────────

export interface ClosedPosition {
  id: number
  symbol: string
  direction: 'long' | 'short'
  entry_price: number
  exit_price: number | null
  quantity: number
  leverage: number
  realized_pnl: number | null
  realized_pnl_pct: number | null
  exit_reason: string | null
  dca_level: number
  opened_at: string
  closed_at: string | null
  duration_minutes: number | null
}

export interface ClosedPositionsResponse {
  items: ClosedPosition[]
  total: number
  limit: number
  offset: number
}

export interface PositionDetail {
  id: number
  symbol: string
  direction: 'long' | 'short'
  is_open: boolean
  strategy_id: number | null
  entry_price: number
  exit_price: number | null
  current_price: number | null
  quantity: number
  leverage: number
  margin_used: number | null
  take_profit_price: number | null
  stop_loss_price: number | null
  unrealized_pnl: number | null
  unrealized_pnl_pct: number | null
  realized_pnl: number | null
  realized_pnl_pct: number | null
  exit_reason: string | null
  peak_price: number | null
  peak_pnl_pct: number | null
  trough_price: number | null
  trough_pnl_pct: number | null
  max_favorable_pct: number | null
  max_adverse_pct: number | null
  entry_fee: number | null
  exit_fee: number | null
  funding_paid: number | null
  dca_level: number
  opened_at: string
  closed_at: string | null
  duration_minutes: number | null
}

// ─── Signal history + decision trace ─────────────────────────────────

export interface HistorySignal {
  id: number
  ts: string
  symbol: string
  direction: 'long' | 'short'
  signal_type: string
  timeframe: string
  score: number | null
  dp_pct: number | null
  doi_pct: number | null
  matched_for_me: boolean
}

export interface HistorySignalsResponse {
  items: HistorySignal[]
  total: number
  limit: number
  offset: number
}

export interface RejectionEntry {
  strategy_id?: number
  user_id?: number
  filter?: string
  detail?: string
  stage?: string
  [k: string]: unknown
}

export interface PassedEntry {
  strategy_id?: number
  user_id?: number
  priority?: number
  [k: string]: unknown
}

export interface SignalDecision {
  signal_id: number
  decided_at: string
  final_outcome: string
  final_rejection_reason: string | null
  candidates_considered: number
  rejections_mine: RejectionEntry[]
  passed_mine: PassedEntry[]
  matched_strategy_id: number | null
  realized_pnl_pct: number | null
  realized_exit_reason: string | null
  position_closed_at: string | null
  opened_position_id: number | null
}

export interface SignalDetail {
  id: number
  ts: string
  symbol: string
  exchange: string
  direction: 'long' | 'short' | 'neutral'
  signal_type: string
  timeframe: string | null
  score: number | null
  // Headline scalars
  dp_pct: number | null
  doi_pct: number | null
  doi_abs_usd: number | null
  current_price: number | null
  oi_current: number | null
  // Volume / liquidity
  rvol: number | null
  rvol_5m: number | null
  rvol_15m: number | null
  volume_oi_ratio: number | null
  spread_pct: number | null
  orderbook_imbalance: number | null
  // CVD
  cvd_1h: number | null
  cvd_divergence: string | null
  cvd_divergence_strength: number | null
  // Funding / trend
  funding_rate: number | null
  funding_bias: string | null
  trend_1h: string | null
  trend_4h: string | null
  htf_direction: string | null
  // Candle structure
  candle_body_pct: number | null
  upper_wick_pct: number | null
  lower_wick_pct: number | null
  // BTC context
  btc_market_phase: string | null
  btc_change_1h_pct: number | null
  btc_is_dumping: boolean | null
  btc_is_pumping: boolean | null
  // ML
  ml_score_entry: number | null
  ml_direction: string | null
  ml_direction_score: number | null
  matched_for_me: boolean
  context_payload: Record<string, unknown>
}

// ─── Queries ──────────────────────────────────────────────────────────

export function useDashboardSummary() {
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: async () => {
      const { data } = await api.get<DashboardSummary>('/dashboard/summary')
      return data
    },
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
}

export function useEquityCurve(days: number = 7) {
  return useQuery({
    queryKey: ['dashboard', 'equity', days],
    queryFn: async () => {
      const { data } = await api.get<EquityCurveResponse>(
        '/dashboard/equity-curve', { params: { days } },
      )
      return data
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useMarketState() {
  return useQuery({
    queryKey: ['dashboard', 'market'],
    queryFn: async () => {
      const { data } = await api.get<MarketState>('/dashboard/market')
      return data
    },
    staleTime: 20_000,
    refetchInterval: 20_000,
  })
}

export function useRiskUsage() {
  return useQuery({
    queryKey: ['dashboard', 'risk'],
    queryFn: async () => {
      const { data } = await api.get<RiskUsage>('/dashboard/risk')
      return data
    },
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
}

export function useOpenPositions() {
  return useQuery({
    queryKey: ['positions', 'open'],
    queryFn: async () => {
      const { data } = await api.get<OpenPositionsResponse>('/positions/open')
      return data
    },
    staleTime: 5_000,
    refetchInterval: 5_000,
  })
}

export function useRecentSignals(limit: number = 30) {
  return useQuery({
    queryKey: ['signals', 'recent', limit],
    queryFn: async () => {
      const { data } = await api.get<RecentSignalsResponse>(
        '/signals/recent', { params: { limit } },
      )
      return data
    },
    staleTime: 5_000,
    refetchInterval: 7_000,
  })
}

// ─── Phase 5: closed positions + detail + signals history + decision ──

export interface ClosedPositionsFilters {
  limit?: number
  offset?: number
  symbol?: string
  direction?: 'long' | 'short'
  exit_reason?: string
  days?: number
}

export function useClosedPositions(filters: ClosedPositionsFilters = {}) {
  return useQuery({
    queryKey: ['positions', 'closed', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      }
      if (filters.symbol)      params.symbol = filters.symbol
      if (filters.direction)   params.direction = filters.direction
      if (filters.exit_reason) params.exit_reason = filters.exit_reason
      if (filters.days)        params.days = filters.days
      const { data } = await api.get<ClosedPositionsResponse>(
        '/positions/closed', { params },
      )
      return data
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export function usePositionDetail(positionId: number | null) {
  return useQuery({
    queryKey: ['positions', 'detail', positionId],
    queryFn: async () => {
      const { data } = await api.get<PositionDetail>(`/positions/${positionId}`)
      return data
    },
    enabled: positionId != null,
    staleTime: 10_000,
  })
}

export interface SignalHistoryFilters {
  limit?: number
  offset?: number
  symbol?: string
  direction?: 'long' | 'short'
  signal_type?: string
  timeframe?: string
  matched?: boolean
  days?: number
}

export function useSignalHistory(filters: SignalHistoryFilters = {}) {
  return useQuery({
    queryKey: ['signals', 'history', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        limit: filters.limit ?? 50,
        offset: filters.offset ?? 0,
      }
      if (filters.symbol)      params.symbol = filters.symbol
      if (filters.direction)   params.direction = filters.direction
      if (filters.signal_type) params.signal_type = filters.signal_type
      if (filters.timeframe)   params.timeframe = filters.timeframe
      if (filters.matched != null) params.matched = filters.matched
      if (filters.days)        params.days = filters.days
      const { data } = await api.get<HistorySignalsResponse>(
        '/signals/history', { params },
      )
      return data
    },
    staleTime: 15_000,
    refetchInterval: 15_000,
  })
}

export function useSignalDecision(signalId: number | null) {
  return useQuery({
    queryKey: ['signals', 'decision', signalId],
    queryFn: async () => {
      const { data } = await api.get<SignalDecision>(
        `/signals/${signalId}/decision`,
      )
      return data
    },
    enabled: signalId != null,
    staleTime: 60_000,
    // 404 = no decision row exists yet (matcher hasn't seen it).
    // We don't want react-query to retry hard on that.
    retry: false,
  })
}

export function useSignalDetail(signalId: number | null) {
  return useQuery({
    queryKey: ['signals', 'detail', signalId],
    queryFn: async () => {
      const { data } = await api.get<SignalDetail>(`/signals/${signalId}`)
      return data
    },
    enabled: signalId != null,
    staleTime: 30_000,
    retry: false,
  })
}

// ─── System events (annotations layer, PULSE-42) ────────────────────

export type EventType =
  | 'strategy' | 'research'
  | 'deploy' | 'bug' | 'config_change' | 'test'
  | 'market_event' | 'incident' | 'note'

export interface SystemEvent {
  id: number
  ts_start: string
  ts_end: string | null
  type: EventType
  title: string
  description: string | null
  color: string | null
  metadata: Record<string, unknown>
  created_by: number | null
  created_at: string
  updated_at: string
}

export interface EventsResponse {
  items: SystemEvent[]
  total: number
}

export interface EventsFilters {
  from?: string
  to?: string
  type?: EventType
  limit?: number
  offset?: number
}

export function useEvents(filters: EventsFilters = {}) {
  return useQuery({
    queryKey: ['events', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        limit: filters.limit ?? 200,
        offset: filters.offset ?? 0,
      }
      if (filters.from) params.from = filters.from
      if (filters.to)   params.to = filters.to
      if (filters.type) params.type = filters.type
      const { data } = await api.get<EventsResponse>('/events', { params })
      return data
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export interface CreateEventInput {
  ts_start: string
  ts_end?: string | null
  type: EventType
  title: string
  description?: string
  color?: string
  metadata?: Record<string, unknown>
}

export async function createEvent(input: CreateEventInput): Promise<SystemEvent> {
  const { data } = await api.post<SystemEvent>('/events', input)
  return data
}

export async function deleteEvent(id: number): Promise<void> {
  await api.delete(`/events/${id}`)
}

// ─── Symbols (PULSE-44) ───────────────────────────────────────────────

export interface SymbolRow {
  exchange: string
  symbol: string
  is_active: boolean
  min_oi_usd: number | null
  avg_volume_24h: number | null
  last_seen: string | null
  open_positions: number
  signals_24h: number
  last_signal_at: string | null
}

export interface SymbolsResponse {
  items: SymbolRow[]
  total: number
  limit: number
  offset: number
}

export interface SymbolsFilters {
  active_only?: boolean
  with_positions?: boolean
  with_signals?: boolean
  search?: string
  limit?: number
  offset?: number
}

export function useSymbols(filters: SymbolsFilters = {}) {
  return useQuery({
    queryKey: ['symbols', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {
        limit: filters.limit ?? 200,
        offset: filters.offset ?? 0,
      }
      if (filters.active_only)    params.active_only = true
      if (filters.with_positions) params.with_positions = true
      if (filters.with_signals)   params.with_signals = true
      if (filters.search)         params.search = filters.search
      const { data } = await api.get<SymbolsResponse>('/symbols', { params })
      return data
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export interface SymbolDetail {
  exchange: string
  symbol: string
  is_active: boolean
  min_oi_usd: number | null
  avg_volume_24h: number | null
  last_seen: string | null
  open_positions_count: number
  signals_24h: number
  last_signal_at: string | null
  signal_types_24h: Array<{ type: string; count: number }>
}

export function useSymbolDetail(symbol: string | null) {
  return useQuery({
    queryKey: ['symbols', 'detail', symbol],
    queryFn: async () => {
      const { data } = await api.get<SymbolDetail>(`/symbols/${symbol}`)
      return data
    },
    enabled: !!symbol,
    staleTime: 30_000,
    retry: false,
  })
}

// ─── History (PULSE-45) ───────────────────────────────────────────────

export type HistoryBucketKind = 'day' | 'week' | 'month'

export interface HistoryBucket {
  bucket_start: string
  trades: number
  wins: number
  losses: number
  win_rate_pct: number | null
  total_pnl: number
  avg_trade_pnl: number | null
  best_trade_pnl: number | null
  worst_trade_pnl: number | null
}

export interface HistorySummaryResponse {
  bucket: HistoryBucketKind
  items: HistoryBucket[]
}

export interface HistoryTrade {
  id: number
  symbol: string
  direction: 'long' | 'short'
  opened_at: string
  closed_at: string
  duration_minutes: number | null
  realized_pnl: number
  realized_pnl_pct: number | null
  exit_reason: string | null
}

export interface HistoryTradesResponse {
  items: HistoryTrade[]
  total: number
}

export function useHistorySummary(
  bucket: HistoryBucketKind = 'day',
  fromIso?: string,
) {
  return useQuery({
    queryKey: ['history', 'summary', bucket, fromIso],
    queryFn: async () => {
      const params: Record<string, unknown> = { bucket, limit: 365 }
      if (fromIso) params.from = fromIso
      const { data } = await api.get<HistorySummaryResponse>(
        '/history/summary', { params },
      )
      return data
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

export function useHistoryTrades(
  fromIso: string | null,
  toIso: string | null,
) {
  return useQuery({
    queryKey: ['history', 'trades', fromIso, toIso],
    queryFn: async () => {
      const { data } = await api.get<HistoryTradesResponse>(
        '/history/trades',
        { params: { from: fromIso, to: toIso, limit: 500 } },
      )
      return data
    },
    enabled: !!fromIso && !!toIso,
    staleTime: 60_000,
    retry: false,
  })
}

// ─── System config (PULSE-46) ─────────────────────────────────────────

export type ConfigValueType = 'bool' | 'int' | 'float' | 'string'
export type ConfigCategory =
  | 'flags' | 'risk' | 'detector' | 'ml' | 'integrations'

export interface ConfigItem {
  key: string
  category: ConfigCategory
  value_text: string
  value_type: ConfigValueType
  description: string | null
  requires_restart: boolean
  // PULSE-58: secret rows (Telegram bot token, future API keys) come
  // back from GET with `value_text` already masked. The flag lets the
  // UI render a password-style input and an explicit "Replace" flow
  // instead of showing the (masked) value as-is.
  is_secret: boolean
  updated_at: string
  updated_by: number | null
}

export interface ConfigListResponse {
  items: ConfigItem[]
}

export function useConfig() {
  return useQuery({
    queryKey: ['admin', 'config'],
    queryFn: async () => {
      const { data } = await api.get<ConfigListResponse>('/admin/config')
      return data
    },
    staleTime: 30_000,
  })
}

export async function updateConfig(key: string, value_text: string): Promise<ConfigItem> {
  const { data } = await api.patch<ConfigItem>(
    `/admin/config/${encodeURIComponent(key)}`,
    { value_text },
  )
  return data
}

// ─── Trading settings + CrashGuard (PULSE-52) ─────────────────────────

export interface TradingSettings {
  // Position management
  trailing_stop_enabled: boolean
  trailing_stop_activation_pct: number
  trailing_stop_distance_pct: number
  dump_protection_enabled: boolean
  dump_threshold_pct: number
  dump_window_minutes: number
  dump_action: 'close' | 'skip_dca'
  price_position_filter_enabled: boolean
  price_position_long_block_above: number
  price_position_short_block_below: number
  price_position_lookback_days: number
  // DCA
  dca_enabled: boolean
  dca_step_mode: 'legacy' | 'multistep'
  dca_steps_pct: number[]
  dca_sizes_pct: number[]
  dca_max_steps: number
  dca_leverage: number
  dca_max_loss_pct: number
  dca_max_hold_minutes: number
  dca_tp_pct: number
  dca_disable_trailing: boolean
  dca_bounce_pct: number | null
  dca_bounce_timeout_minutes: number
  dca_bounce_from_step: number
  // Regime gates
  dca_disabled_directions_in_phase: Record<string, string[]>
  stress_dca_block_enabled: boolean
  stress_oi_threshold: number | null
  stress_oi_window_hours: number | null
  ml_direction_filter_signal_types: string[]
  // Risk
  cb_global_daily_loss_pct: number
  sl_coin_block_enabled: boolean
  sl_coin_block_minutes: number
  per_symbol_daily_loss_limit: number | null
  // Global sizing overrides (PULSE-66). Both null = use per-strategy.
  global_position_size_pct: number | null
  global_leverage_override: number | null
  // Discovery
  discovery_default_leverage: number
  discovery_default_position_size_pct: number
  discovery_excluded_slots: string[]
  // Metadata
  updated_at: string
  updated_by: string | null
}

// Trading parameters are GLOBAL by design — single row applies to
// every account. Per-account knobs (API keys, telegram, risk caps)
// live on `trading_users` and are managed via `/api/me/*`.
export function useTradingSettings() {
  return useQuery({
    queryKey: ['admin', 'config', 'trading'],
    queryFn: async () => {
      const { data } = await api.get<TradingSettings>('/admin/config/trading')
      return data
    },
    staleTime: 15_000,
  })
}

export async function patchTradingSettings(
  patch: Partial<TradingSettings>,
): Promise<TradingSettings> {
  const { data } = await api.patch<TradingSettings>(
    '/admin/config/trading', patch,
  )
  return data
}

export interface CrashGuardSettings {
  enabled: boolean
  oi_drop_pct: number
  liq_cascade_usd: number
  cvd_reversal_negative: number
  cvd_reversal_positive: number
  volume_spike_multiplier: number
  funding_threshold_bps: number
  squeeze_liq_ratio: number
  block_duration_minutes: number
  block_direction: 'long' | 'short' | 'both'
  min_triggers: number
}

export interface CrashGuardResponse {
  settings: CrashGuardSettings
  events_today: number
  last_trigger: string | null
}

export interface CrashGuardEvent {
  id: number
  trigger_type: string
  trigger_value: number | null
  threshold_value: number | null
  description: string | null
  blocked_direction: string | null
  blocked_symbol: string | null
  btc_price: number | null
  created_at: string
}

export function useCrashGuard() {
  return useQuery({
    queryKey: ['admin', 'config', 'crashguard'],
    queryFn: async () => {
      const { data } = await api.get<CrashGuardResponse>('/admin/config/crashguard')
      return data
    },
    staleTime: 15_000,
  })
}

export async function updateCrashGuard(
  settings: CrashGuardSettings,
): Promise<CrashGuardResponse> {
  const { data } = await api.put<CrashGuardResponse>(
    '/admin/config/crashguard', settings,
  )
  return data
}

export function useCrashGuardEvents(days = 7, limit = 20) {
  return useQuery({
    queryKey: ['admin', 'config', 'crashguard', 'events', days, limit],
    queryFn: async () => {
      const { data } = await api.get<{ items: CrashGuardEvent[] }>(
        `/admin/config/crashguard/events?days=${days}&limit=${limit}`,
      )
      return data.items
    },
    staleTime: 60_000,
  })
}

// ─── Slots + Discovery (PULSE-49) ─────────────────────────────────────

export interface SlotRow {
  id: number
  user_id: number
  name: string
  signal_type: string
  timeframe: string
  direction: string | null
  regime_bucket: string | null
  is_active: boolean
  slot_state: string | null
  slot_state_changed_at: string | null
  weak_cycles: number
  rework_count: number
  deactivation_reason: string | null
  last_switch_at: string | null
  total_switches: number
  strategy_id: number | null
  strategy_name: string | null
  strategy_tp_pct: number | null
  strategy_sl_pct: number | null
  strategy_hold_min: number | null
  strategy_is_active: boolean | null
  strategy_backtest_pnl_pct: number | null
  strategy_backtest_wr: number | null
  // A/B sides from strategy_pairs — for slot-level comparison UI.
  strategy_a_id: number | null
  strategy_b_id: number | null
  active_strategy: string | null  // 'a' | 'b'
  probing_strategy_id: number | null
  probing_strategy_name: string | null
  probing_started_at: string | null
  discovery_result_id: number | null
  discovery_run_id: number | null
  discovery_rank: number | null
  discovery_weighted_pnl: number | null
  // Compound: "$100 → $X after backtest 30d", returned as percent growth
  bt_compound_30d_pct: number | null
  bt_trades_30d: number | null
  bt_compound_cycles: number | null
  real_pnl_24h: number | null
  real_trades_24h: number
  real_pnl_7d: number | null
  real_trades_7d: number
}

export interface SlotsResponse {
  items: SlotRow[]
  total: number
}

export interface SlotsFilters {
  signal_type?: string
  timeframe?: string
  direction?: string
  regime?: string
  state?: string
  active_only?: boolean
}

export function useSlots(filters: SlotsFilters = {}) {
  return useQuery({
    queryKey: ['slots', filters],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (filters.signal_type) params.signal_type = filters.signal_type
      if (filters.timeframe)   params.timeframe = filters.timeframe
      if (filters.direction)   params.direction = filters.direction
      if (filters.regime)      params.regime = filters.regime
      if (filters.state)       params.state = filters.state
      if (filters.active_only) params.active_only = true
      const { data } = await api.get<SlotsResponse>('/slots', { params })
      return data
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export interface SlotStateTransition {
  ts: string
  from_state: string | null
  to_state: string | null
  reason: string | null
}

export interface SlotDetail {
  slot: SlotRow
  transitions: SlotStateTransition[]
}

export function useSlotDetail(slotId: number | null) {
  return useQuery({
    queryKey: ['slots', 'detail', slotId],
    queryFn: async () => {
      const { data } = await api.get<SlotDetail>(`/slots/${slotId}`)
      return data
    },
    enabled: slotId != null,
    staleTime: 30_000,
    retry: false,
  })
}

// Discovery

export interface DiscoveryRun {
  id: number
  started_at: string
  completed_at: string | null
  status: string
  run_type: string
  signal_types_tested: string[]
  timeframes_tested: string[]
  total_variations_tested: number
  total_strategies_found: number
  error_message: string | null
}

export interface DiscoveryRunsResponse { items: DiscoveryRun[] }

export function useDetectorGates() {
  return useQuery({
    queryKey: ['lab', 'detector-gates'],
    queryFn: async () => {
      const { data } = await api.get<any>('/lab/detector-gates')
      return data
    },
    staleTime: 120_000,
    refetchInterval: 300_000,
  })
}

export function usePipelineLatest() {
  return useQuery({
    queryKey: ['discovery', 'pipeline-latest'],
    queryFn: async () => {
      const { data } = await api.get<any>('/discovery/pipeline/latest')
      return data
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  })
}

export function useFleetHealth() {
  return useQuery({
    queryKey: ['discovery', 'fleet-health'],
    queryFn: async () => {
      const { data } = await api.get<any>('/discovery/fleet-health')
      return data
    },
    staleTime: 60_000,
    refetchInterval: 300_000,
  })
}

export function useDiscoveryRuns(limit: number = 50) {
  return useQuery({
    queryKey: ['discovery', 'runs', limit],
    queryFn: async () => {
      const { data } = await api.get<DiscoveryRunsResponse>(
        '/discovery/runs', { params: { limit } },
      )
      return data
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

// On-demand discovery trigger — calls POST /discovery/trigger. The
// server spawns a fresh `pulsar-discovery` container and returns 202
// immediately; progress shows up in the runs list within a few seconds
// (status='running'), then 'completed' when the worker finishes.
export interface DiscoveryTriggerResponse {
  status: 'started' | 'already_running'
  container_id?: string
  run_type?: 'daily' | 'targeted'
  started_at?: string
  in_flight_run_id?: number
  in_flight_minutes?: number
}

export async function triggerDiscovery(
  run_type: 'daily' | 'targeted' = 'daily',
  opts: { targeted_slots?: string[]; expanded?: boolean; full?: boolean } = {},
): Promise<DiscoveryTriggerResponse> {
  const { data } = await api.post<DiscoveryTriggerResponse>(
    '/discovery/trigger',
    { run_type, ...opts },
  )
  return data
}

export interface DiscoveryResult {
  id: number
  run_id: number
  rank: number
  signal_type: string
  timeframe: string
  direction: string | null
  move_type: string | null
  regime_bucket: string | null
  tier: string | null
  continuation_mode: boolean
  take_profit_pct: number
  stop_loss_pct: number
  max_hold_minutes: number
  pnl_1d: number | null
  pnl_3d: number | null
  pnl_10d: number | null
  pnl_30d: number | null
  win_rate_1d: number | null
  win_rate_3d: number | null
  win_rate_10d: number | null
  win_rate_30d: number | null
  trades_1d: number
  trades_3d: number
  trades_10d: number
  trades_30d: number
  weighted_pnl: number | null
  stability_score: number | null
  regime_bonus: number | null
  recommendation: string
  compared_to_strategy_id: number | null
  improvement_pct: number | null
  signal_filters: Record<string, unknown>
  pump_dump_filter: string | null
  // ID of the live trading_strategies row created by auto-apply
  // for this result. null ⇒ apply skipped it.
  applied_strategy_id: number | null
}

export interface DiscoveryResultsResponse {
  items: DiscoveryResult[]
  run_id: number
}

export function useDiscoveryResults(runId: number | null, limit: number = 500) {
  return useQuery({
    queryKey: ['discovery', 'results', runId, limit],
    queryFn: async () => {
      const { data } = await api.get<DiscoveryResultsResponse>(
        `/discovery/runs/${runId}/results`, { params: { limit } },
      )
      return data
    },
    enabled: runId != null,
    staleTime: 60_000,
  })
}

// ─── User-scoped settings (PULSE-55) ──────────────────────────────────

export interface MeSubAccount {
  id: number
  account_label: string | null
  bybit_testnet: boolean
  has_api_keys: boolean
  is_active: boolean
  exclude_from_apply: boolean
}

export interface MeSettings {
  id: number
  email: string | null
  role: string
  status: string
  is_master: boolean
  account_label: string | null
  has_api_keys: boolean
  bybit_testnet: boolean
  api_key_masked: string | null
  max_concurrent_positions: number
  daily_loss_limit_pct: number
  telegram_chat_id: string | null
  telegram_enabled: boolean
  notify_on_open: boolean
  notify_on_close: boolean
  notify_on_error: boolean
  created_at: string
  last_login_at: string | null
  sub_accounts: MeSubAccount[]
}

// Helper — append `?account_id=N` when an explicit target is set.
// Empty string when targeting self so URL stays clean.
function _scope(accountId?: number | null): string {
  return accountId == null ? '' : `?account_id=${accountId}`
}

export function useMeSettings(accountId?: number | null) {
  return useQuery({
    queryKey: ['me', 'settings', accountId ?? 'self'],
    queryFn: async () => {
      const { data } = await api.get<MeSettings>(`/me/settings${_scope(accountId)}`)
      return data
    },
    staleTime: 10_000,
  })
}

export async function patchMeSettings(
  patch: Partial<MeSettings>,
  accountId?: number | null,
): Promise<MeSettings> {
  const { data } = await api.patch<MeSettings>(
    `/me/settings${_scope(accountId)}`, patch,
  )
  return data
}

export async function putApiKeys(
  api_key: string, api_secret: string, testnet: boolean,
  accountId?: number | null,
): Promise<MeSettings> {
  const { data } = await api.put<MeSettings>(
    `/me/api-keys${_scope(accountId)}`,
    { api_key, api_secret, testnet },
  )
  return data
}

export async function toggleTestnet(
  testnet: boolean, accountId?: number | null,
): Promise<MeSettings> {
  const { data } = await api.post<MeSettings>(
    `/me/api-keys/toggle-testnet${_scope(accountId)}`, { testnet },
  )
  return data
}

export interface TelegramTestResult {
  success: boolean
  message: string | null
  error: string | null
}

export async function testTelegram(
  accountId?: number | null,
): Promise<TelegramTestResult> {
  const { data } = await api.post<TelegramTestResult>(
    `/me/telegram/test${_scope(accountId)}`,
  )
  return data
}

export async function patchSubAccount(
  sub_id: number,
  patch: { account_label?: string; is_active?: boolean; exclude_from_apply?: boolean },
): Promise<MeSubAccount> {
  const { data } = await api.patch<MeSubAccount>(
    `/me/sub-accounts/${sub_id}`, patch,
  )
  return data
}

export async function createSubAccount(
  payload: { account_label: string; bybit_testnet: boolean; exclude_from_apply?: boolean },
): Promise<MeSubAccount> {
  const { data } = await api.post<MeSubAccount>('/me/sub-accounts', payload)
  return data
}

export interface UserRiskBlock {
  id: number
  user_id: number
  strategy_id: number | null
  strategy_name: string | null
  block_type: string
  reason: string
  blocked_until: string
  consecutive_losses: number | null
  loss_amount: number | null
  loss_pct: number | null
  created_at: string
}

export function useMyRiskBlocks() {
  return useQuery({
    queryKey: ['me', 'risk-blocks'],
    queryFn: async () => {
      const { data } = await api.get<{ items: UserRiskBlock[] }>(
        '/me/risk-blocks',
      )
      return data.items
    },
    staleTime: 30_000,
  })
}

export async function clearMyRiskBlock(block_id: number): Promise<void> {
  await api.delete(`/me/risk-blocks/${block_id}`)
}

// ─── Bybit account health (PULSE-28) ──────────────────────────────────

export interface BybitAccountAlert {
  severity: 'red' | 'yellow'
  type: 'key_invalid' | 'key_expiring'
  message: string
  days_until_expiry: number | null
}

export interface BybitAccountStatus {
  has_api_keys: boolean
  key_valid: boolean
  key_expires_at: string | null
  key_expires_in_days: number | null
  last_check_at: string | null
  last_check_error: string | null
  balance_usdt: number | null
  balance_available_usdt: number | null
  balance_unrealised_pnl: number | null
  alerts: BybitAccountAlert[]
}

// Hourly cron refreshes the row, so we poll at the same cadence as
// other slow-moving dashboard cards (60s). Banner + card share the
// single query — react-query dedupes.
export function useBybitStatus(accountId?: number | null) {
  return useQuery({
    queryKey: ['me', 'bybit-status', accountId ?? 'self'],
    queryFn: async () => {
      const { data } = await api.get<BybitAccountStatus>(
        `/me/bybit-status${_scope(accountId)}`,
      )
      return data
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

// ─── Pulsar LAB (PULSE-172) ──────────────────────────────────────────
// Read-only forward-validation dashboard for the 6 manual lab
// strategies. Account-agnostic (fixed lab user) — no account param.

export interface LabExpectation {
  expected_mean_pp: number | null
  expected_wr: number | null          // 0..1
  sample_n: number | null
  confidence: string | null
  window: string | null
  note: string | null
  thesis_ru: string | null            // RU user-facing card thesis
}

export interface LabLiveStats {
  n_closed: number
  n_open: number
  win_rate: number | null              // 0..1 over closed trades
  total_realized_pnl_pct: number | null
  avg_realized_pnl_pct: number | null
  last_opened_at: string | null
}

// Last closed trade vs its tick-replay backtest (cron-updated). divergence_pp = bt - real.
export interface LabLastTradeRecon {
  symbol: string | null
  opened_at: string | null
  account_label: string | null
  is_real: boolean | null
  real_pnl: number | null
  bt_pnl: number | null
  divergence_pp: number | null
  real_reason: string | null
  bt_reason: string | null
  reason_match: boolean | null
  note: string | null
  computed_at: string | null
}

export interface LabStrategy {
  strategy_id: number
  name: string
  signal_type: string | null
  trade_direction: string | null       // 'long' | 'short'
  description: string | null
  is_active: boolean
  take_profit_pct: number | null
  stop_loss_pct: number | null
  max_hold_minutes: number | null
  cooldown_minutes: number | null
  leverage: number | null
  position_size_pct: number | null
  version: number | null
  expectation: LabExpectation | null
  live: LabLiveStats
  last_trade_recon: LabLastTradeRecon | null
}

export interface LabStrategiesResponse {
  items: LabStrategy[]
  total: number
}

export function useLabStrategies() {
  return useQuery({
    queryKey: ['lab', 'strategies'],
    queryFn: async () => {
      const { data } = await api.get<LabStrategiesResponse>('/lab/strategies')
      return data
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })
}

export interface LabTradesResponse {
  trades: LabTrade[]
}

export function useLabTrades(strategyId?: number, symbol?: string, limit = 60) {
  return useQuery({
    queryKey: ['lab', 'trades', strategyId ?? 'all', symbol ?? 'all', limit],
    queryFn: async () => {
      const { data } = await api.get<LabTradesResponse>('/lab/trades', {
        params: {
          strategy_id: strategyId ?? undefined,
          symbol: symbol || undefined,
          limit,
        },
      })
      return data
    },
    staleTime: 15_000,
    refetchInterval: 20_000,
  })
}

// ─── PULSE-215: symbols traded by lab strategies (coin picker) ──────
export interface LabTradeSymbol {
  symbol: string
  trades: number
}

export interface LabTradeSymbolsResponse {
  symbols: LabTradeSymbol[]
}

export function useLabTradeSymbols() {
  return useQuery({
    queryKey: ['lab', 'trade-symbols'],
    queryFn: async () => {
      const { data } = await api.get<LabTradeSymbolsResponse>('/lab/trade-symbols')
      return data
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
}

// ─── Pulsar LAB — strategy detail (PULSE-200) ───────────────────────
// GET /lab/strategies/{id} — full dossier for one manual lab strategy:
// config + filters + expectation + live stats + weekly/by-symbol +
// version history + recent trades + execution errors.

export interface LabVersion {
  version_no: number
  change_reason: string
  change_source: string | null
  created_at: string
}

export interface LabTrade {
  position_id: number | null
  strategy_name: string | null
  symbol: string
  direction: string
  opened_at: string | null
  closed_at: string | null
  is_open: boolean
  entry_price: number | null
  realized_pnl_pct: number | null
  unrealized_pnl_pct: number | null
  exit_reason: string | null
  // PULSE-222: which account took this trade (one strategy → N accounts)
  user_id?: number | null
  account_label?: string | null
  is_real?: boolean | null
}

// ─── PULSE-219: trade share-card ────────────────────────────────────
export interface SharedTradeData {
  trade: {
    symbol: string; direction: string; strategy_name: string | null
    account_label: string | null; is_open: boolean
    entry_price: number | null; exit_price: number | null
    realized_pnl_pct: number | null; unrealized_pnl_pct: number | null
    opened_at: string | null; closed_at: string | null; exit_reason: string | null
    leverage: number | null; take_profit_price: number | null
    stop_loss_price: number | null; max_hold_minutes: number | null
  }
  tf: string
  entry_marker: { t: number; price: number } | null
  exit_marker: { t: number; price: number } | null
  series: import('@/hooks/useMarketApi').MarketSeries
}

export function useSharedTrade(token: string | undefined) {
  return useQuery({
    queryKey: ['share', 'trade', token],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await api.get<SharedTradeData>(`/share/trade/${token}`)
      return data
    },
    staleTime: 60_000,
    retry: false,
  })
}

export function useCreateTradeShare() {
  return useMutation({
    mutationFn: async (positionId: number) => {
      const { data } = await api.post<{ token: string; url: string }>('/share/trade', { position_id: positionId })
      return data
    },
  })
}

// ─── PULSE-220: public coin summary (charts + liq heatmap) ──────────
export interface CoinSummaryData {
  symbol: string
  tf: string
  series: import('@/hooks/useMarketApi').MarketSeries
  heatmap: import('@/hooks/useMarketApi').MarketHeatmap
}

export function useCoinSummary(token: string | undefined, tf?: string, at?: number) {
  return useQuery({
    queryKey: ['share', 'coin', token, tf ?? '', at ?? 0],
    enabled: !!token,
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data } = await api.get<CoinSummaryData>(`/share/coin/${token}`, { params: { ...(tf ? { tf } : {}), ...(at ? { at } : {}) } })
      return data
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
    retry: false,
  })
}

export function useCreateCoinShare() {
  return useMutation({
    mutationFn: async (symbol: string) => {
      const { data } = await api.post<{ token: string; url: string }>('/share/coin', { symbol })
      return data
    },
  })
}

export interface LabError {
  symbol: string | null
  status: string | null
  created_at: string | null
  error_message: string | null
}

export interface LabWeekly {
  week: string                          // 'YYYY-MM-DD' (week start)
  n: number
  avg_pnl: number | null
}

export interface LabSymbolStat {
  symbol: string
  n: number
  avg_pnl: number | null
  total_pnl: number | null
}

export interface LabStrategyDetail {
  strategy_id: number
  name: string
  signal_type: string | null
  signal_types: string[]
  trade_direction: string | null
  signal_direction_filter: string | null
  description: string | null
  is_active: boolean
  managed_by: string | null
  timeframes: string[]
  min_score: number | null
  take_profit_pct: number | null
  stop_loss_pct: number | null
  trailing_stop_enabled: boolean | null
  trailing_stop_activation_pct: number | null
  trailing_stop_distance_pct: number | null
  max_hold_minutes: number | null
  cooldown_minutes: number | null
  leverage: number | null
  position_size_pct: number | null
  entry_type: string | null
  max_consecutive_losses: number | null
  max_entry_slippage_pct: number | null
  dca_enabled: boolean | null
  dca_disable_trailing: boolean | null
  signal_filters: Record<string, unknown> | null
  current_version_id: number | null
  created_at: string | null
  updated_at: string | null
  expectation: LabExpectation | null
  live: LabLiveStats
  weekly: LabWeekly[]
  by_symbol: LabSymbolStat[]
  versions: LabVersion[]
  trades: LabTrade[]
  errors: LabError[]
  last_trade_recon: LabLastTradeRecon | null
  archived_at: string | null
  archived_reason: string | null
  // PULSE-230: единое досье лаба+дискавери
  battery: Array<{
    verdict: string
    mean: number | null
    ex_best_week: number | null
    n: number | null
    live_sum: number | null
    live_n: number | null
    checked_at: string
  }>
  assignments: Array<{
    user_id: number
    is_active: boolean
    label: string
    demo: boolean
  }>
}

export function useLabStrategy(id: number | undefined) {
  return useQuery({
    queryKey: ['lab', 'strategy', id],
    enabled: id != null && Number.isFinite(id),
    queryFn: async () => {
      const { data } = await api.get<LabStrategyDetail>(`/lab/strategies/${id}`)
      return data
    },
    staleTime: 20_000,
    refetchInterval: 20_000,
  })
}

// ─── Market state + матрица страта×режим (PULSE-223) ─────────────────

export interface LabMarketState {
  ts: string | null
  btc_ret_1h: number | null
  btc_ret_4h: number | null
  btc_ret_24h: number | null
  btc_vol_6h: number | null
  btc_dd_7d: number | null
  breadth_4h: number | null
  disp_4h: number | null
  alt_relstr_4h: number | null
  mkt_longliq_4h: number | null
  btc_ret_5m?: number | null
  btc_ret_15m?: number | null
  liq_usd_15m?: number | null
  shock_state?: string | null
  mkt_shortliq_4h: number | null
  fng: number | null
  crash: boolean | null
  stale: boolean
}

export interface LabRegimeCell {
  feature: string
  feature_label: string
  bucket: string
  n: number
  avg_pnl: number | null
  win_rate: number | null
  is_current: boolean
  fleet_n: number
  fleet_avg_pnl: number | null
}

export interface LabRegimeMatrix {
  strategy_id: number
  n_tagged: number
  cells: LabRegimeCell[]
  current: LabMarketState | null
}

export function useLabMarketState() {
  return useQuery({
    queryKey: ['lab', 'market-state'],
    queryFn: async () => {
      const { data } = await api.get<LabMarketState>('/lab/market-state')
      return data
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: false,
  })
}

export function useLabRegime(id: number | undefined) {
  return useQuery({
    queryKey: ['lab', 'regime', id],
    enabled: id != null && Number.isFinite(id),
    queryFn: async () => {
      const { data } = await api.get<LabRegimeMatrix>(`/lab/strategies/${id}/regime`)
      return data
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
    retry: false,
  })
}

// ─── Lab strategy — feed health (PULSE-202): is the source alive? ────

export interface LabFeedHealth {
  signal_type: string | null
  producer: string | null
  producer_kind: string               // container | cron | none
  producer_status: string
  producer_note: string | null
  last_signal_at: string | null
  last_age_h: number | null
  count_24h: number
  count_7d: number
  count_30d: number
  avg_interval_h: number | null
  verdict: 'healthy' | 'rare' | 'stale' | 'down' | 'retired' | 'orphaned' | 'unknown' | string
  reason: string
}

export function useLabStrategyFeed(id: number | undefined) {
  return useQuery({
    queryKey: ['lab', 'feed', id],
    enabled: id != null && Number.isFinite(id),
    queryFn: async () => {
      const { data } = await api.get<LabFeedHealth>(`/lab/strategies/${id}/feed`)
      return data
    },
    staleTime: 20_000,
    refetchInterval: 30_000,
    retry: false,
  })
}

// ─── Lab strategy — matcher decision feed (PULSE-200 phase 3) ────────
// What the matcher did with signals THIS strategy saw: matched (won),
// rejected (a filter fired — with reason), or passed (cleared filters
// but another strategy won / a global gate stopped it).

export interface LabSignalDecision {
  decision_id: number
  decided_at: string
  signal_id: number
  symbol: string
  direction: string
  signal_type: string
  timeframe: string | null
  score: number | null
  status: 'matched' | 'rejected' | 'passed' | string
  reason: string | null
  final_outcome: string
  opened_position_id: number | null
  realized_pnl_pct: number | null
  realized_exit_reason: string | null
}

export interface LabSignalsResponse {
  items: LabSignalDecision[]
  total: number
  // Aggregate totals over the lookback window (not just the listed page).
  matched: number
  passed: number
  rejected: number
  window_days: number
}

export function useLabStrategySignals(id: number | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['lab', 'signals', id],
    enabled: enabled && id != null && Number.isFinite(id),
    queryFn: async () => {
      const { data } = await api.get<LabSignalsResponse>(`/lab/strategies/${id}/signals`)
      return data
    },
    staleTime: 20_000,
  })
}

// ─── Container ops panel (PULSE-202, admin-only) ─────────────────────

export interface LabContainer {
  name: string
  label: string
  group: string                       // bot | engine | detector | data | support | infra | other
  signal: string | null
  intent: 'live' | 'retired' | 'unknown' | string
  note: string | null
  primary: boolean
  relevant: boolean                   // tied to an active strategy (default-shown)
  controllable: boolean
  critical: boolean
  status: string                      // running | exited | absent | created | ...
  health: string | null
  exit_code: number | null
  started_at: string | null
  finished_at: string | null
  restart_count: number | null
}

export interface LabContainersResponse {
  items: LabContainer[]
}

export interface LabContainerLogs {
  name: string
  logs: string
}

export function useLabContainers(enabled = true) {
  return useQuery({
    queryKey: ['lab', 'containers'],
    enabled,
    queryFn: async () => {
      const { data } = await api.get<LabContainersResponse>('/lab/containers')
      return data
    },
    staleTime: 8_000,
    refetchInterval: 10_000,
    retry: false,                     // 403 for non-admins → just hide the panel
  })
}

export function useContainerAction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { name: string; action: 'start' | 'restart' }) => {
      const { data } = await api.post<LabContainer>(
        `/lab/containers/${vars.name}/${vars.action}`,
      )
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lab', 'containers'] })
    },
  })
}

export function useContainerLogs(name: string | null, tail = 200) {
  return useQuery({
    queryKey: ['lab', 'containers', name, 'logs', tail],
    enabled: !!name,
    queryFn: async () => {
      const { data } = await api.get<LabContainerLogs>(
        `/lab/containers/${name}/logs`, { params: { tail } },
      )
      return data
    },
    refetchInterval: name ? 5_000 : false,
  })
}

// ─── PULSE-231: сигнал-контекст (график) + share сигнала ─────────────

export interface SignalContextPosition {
  id: number
  user_id: number
  account_label: string
  is_real: boolean
  strategy_name: string | null
  direction: string
  is_open: boolean
  entry_price: number | null
  exit_price: number | null
  realized_pnl_pct: number | null
  unrealized_pnl_pct: number | null
  take_profit_price: number | null
  stop_loss_price: number | null
  opened_at: string | null
  closed_at: string | null
  exit_reason: string | null
  entry_marker: { t: number; price: number } | null
  exit_marker: { t: number; price: number } | null
}

export interface SignalContext {
  signal: Record<string, any>
  signal_marker: { t: number; price: number } | null
  tf: string
  window: string
  series: any
  positions: SignalContextPosition[]
}

export function useSignalContext(id: number | null, window: string) {
  return useQuery({
    queryKey: ['signals', 'context', id, window],
    enabled: id != null,
    queryFn: async () => {
      const { data } = await api.get<SignalContext>(`/signals/${id}/context`, {
        params: { window },
      })
      return data
    },
    staleTime: 30_000,
    retry: false,
  })
}

export function useCreateSignalShare() {
  return useMutation({
    mutationFn: async (signalId: number) => {
      const { data } = await api.post<{ token: string; url: string }>(
        '/share/signal', { signal_id: signalId })
      return data
    },
  })
}

export function useSharedSignal(token: string | undefined) {
  return useQuery({
    queryKey: ['share', 'signal', token],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await api.get<SignalContext>(`/share/signal/${token}`)
      return data
    },
    staleTime: 60_000,
    retry: false,
  })
}
